import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Calendar, 
  Clock, 
  MapPin, 
  User, 
  ArrowLeft,
  Filter,
  Search,
  Video
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { fetchSessions } from '../lib/instantData'
import GlassCard from '../components/ui/GlassCard'
import PanelShell from '../components/ui/PanelShell'
import GradientButton from '../components/ui/GradientButton'

interface UpcomingSession {
  id: string
  title: string
  type: 'online' | 'in-person'
  date: string
  time: string
  trainer: string
  duration: number
  notes?: string
  status: string

  client_join_url?: string // Field for the client join URL
}

const UpcomingSessions: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [sessions, setSessions] = useState<UpcomingSession[]>([])
  const [loading, setLoading] = useState(true)
  const [filterType, setFilterType] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    fetchAllUpcomingSessions()
  }, [user])

  // Poll InstantDB for session updates (Instant syncs live; poll keeps list fresh)
  useEffect(() => {
    if (!user?.id) return
    const interval = setInterval(() => fetchAllUpcomingSessions(), 10000)
    return () => clearInterval(interval)
  }, [user])

  const fetchAllUpcomingSessions = async () => {
    if (!user?.id) return

    try {
      const upcomingData = await fetchSessions({ userId: user.id })
      const formattedSessions = upcomingData
        .filter((s) => ['scheduled', 'cancelled', 'completed'].includes(s.status))
        .map((session) => ({
          id: session.id,
          title: `${session.session_type === 'online' ? 'Online' : 'Private'} Training Session`,
          type: (session.session_type === 'online' ? 'online' : 'in-person') as
            | 'online'
            | 'in-person',
          date: new Date(session.scheduled_date).toLocaleDateString(),
          time: new Date(session.scheduled_date).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
          trainer: 'Cassie',
          duration: session.duration_minutes,
          notes: session.notes,
          status: session.status,
          client_join_url: session.client_join_url,
        }))
      setSessions(formattedSessions)
    } catch (error) {
      console.error('Error fetching all upcoming sessions:', error)
    } finally {
      setLoading(false)
    }
  }


  


  const getTypeIcon = (type: string) => {
    return type === 'online' ? (
      <Video className="h-5 w-5 text-brand" />
    ) : (
      <MapPin className="h-5 w-5 text-gold" />
    )
  }

  const getTypeColor = (type: string) => {
    return type === 'online' 
      ? 'bg-brand-soft text-brand-dark border-brand/20' 
      : 'bg-gold-soft text-ink border-gold/30'
  }

  const getStatusColor = (status: string) => {
    return status === 'cancelled' 
      ? 'bg-red-50 text-red-700 border-red-200'
      : 'bg-gray-50 text-gray-700 border-gray-200'
  }

  const filteredSessions = sessions.filter(session => {
    const matchesType = filterType === 'all' || session.type === filterType
    const matchesSearch = session.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         session.trainer.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesType && matchesSearch
  })

  // Separate active, completed, and cancelled sessions
  const activeSessions = filteredSessions.filter(session => session.status === 'scheduled')
  const completedSessions = filteredSessions.filter(session => session.status === 'completed')
  const cancelledSessions = filteredSessions.filter(session => session.status === 'cancelled')

  if (loading) {
    return (
      <div className="panel-page flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gold"></div>
      </div>
    )
  }

  return (
    <PanelShell
      eyebrow="Training"
      title="Your sessions"
      subtitle="View and manage all your scheduled training sessions."
      actions={
        <GradientButton
          onClick={() => navigate('/dashboard')}
          variant="outline"
          size="sm"
          icon={ArrowLeft}
        >
          Back to dashboard
        </GradientButton>
      }
    >
      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="mb-8"
      >
        <GlassCard>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-600" />
                <input
                  type="text"
                  placeholder="Search sessions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-2">Filter by Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="form-input"
              >
                <option value="all">All Types</option>
                <option value="online">Online Sessions</option>
                <option value="in-person">Private Sessions</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilterType('all')
                  setSearchTerm('')
                }}
                className="btn-secondary w-full text-sm py-2.5"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </GlassCard>
      </motion.div>

              {/* Active Sessions Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="font-display text-2xl font-semibold text-ink mb-2">Upcoming Sessions</h2>
            <p className="text-gray-600">Your scheduled training sessions</p>
          </div>
          <button
            onClick={fetchAllUpcomingSessions}
            className="btn-gold text-sm px-5 py-2.5"
            title="Refresh sessions to check if trainer has joined"
          >
            Refresh
          </button>
        </div>
        {activeSessions.length === 0 ? (
          <GlassCard className="text-center">
            <Calendar className="h-16 w-16 mx-auto text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-ink mb-2">
              {sessions.length === 0 ? 'No Upcoming Sessions' : 'No Active Sessions Found'}
            </h3>
            <p className="text-gray-600">
              {sessions.length === 0 
                ? 'Schedule your first training session to get started!'
                : 'Try adjusting your search or filter criteria.'
              }
            </p>
          </GlassCard>
        ) : (
          <div className="grid gap-6">
            {activeSessions.map((session, index) => (
              <motion.div
                key={session.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                                   <GlassCard className="hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="flex-shrink-0">
                        <div className="p-3">
                          {getTypeIcon(session.type)}
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className={`text-xl font-semibold ${session.status === 'cancelled' ? 'text-gray-500 line-through' : 'text-ink'}`}>
                            {session.title}
                          </h3>
                          <div className="flex items-center space-x-2">
                            {session.status === 'cancelled' && (
                              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(session.status)}`}>
                                Cancelled
                              </span>
                            )}
                            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getTypeColor(session.type)}`}>
                              {session.type === 'online' ? 'Online' : 'Private'}
                            </span>
                            {/* Show invitation status for online sessions */}
                            {session.type === 'online' && 
                             session.status === 'scheduled' && 
                             session.client_join_url && (
                              <span className="px-3 py-1 rounded-full text-sm font-medium border bg-gold-soft text-ink border-gold/30">
                                Invited
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="flex items-center text-gray-600">
                            <Calendar className="h-5 w-5 mr-2 flex-shrink-0" />
                            <span className="text-base">{session.date}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Clock className="h-5 w-5 mr-2 flex-shrink-0" />
                            <span className="text-base">{session.time}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Clock className="h-5 w-5 mr-2 flex-shrink-0" />
                            <span className="text-base">{session.duration} min</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <User className="h-5 w-5 mr-2 flex-shrink-0" />
                            <span className="text-base">with {session.trainer}</span>
                          </div>
                        </div>

                                                  <div className="flex items-center justify-end space-x-2">
                          {session.status === 'cancelled' && (
                            <span className="px-4 py-2 text-gray-500 text-sm italic">
                              Session Cancelled
                            </span>
                          )}
                          
                          {/* Show Join Call button for any non-empty client_join_url (including localhost, for local dev) */}
                          {session.type === 'online' && 
                           session.status === 'scheduled' && 
                           session.client_join_url && (
                            <button
                              onClick={() => window.open(session.client_join_url, '_blank')}
                              className="btn-primary text-sm px-5 py-2.5 gap-2"
                            >
                              <Video className="h-4 w-4" />
                              <span>Join Call</span>
                            </button>
                          )}
                        </div>

                        {session.notes && (
                          <div className="mt-4 p-3 bg-cream rounded-2xl border border-gray-100">
                            <p className="text-sm text-gray-600">
                              <strong>Notes:</strong> {session.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Completed Sessions Grid */}
      {completedSessions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12"
        >
          <div className="mb-6">
            <h2 className="font-display text-2xl font-semibold text-ink mb-2">Completed Sessions</h2>
            <p className="text-gray-600">Sessions that have been completed</p>
          </div>
          <div className="grid gap-6">
            {completedSessions.map((session, index) => (
              <motion.div
                key={session.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <GlassCard className="hover:shadow-xl transition-all duration-300 border-green-200 bg-green-50/10">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="flex-shrink-0">
                        <div className="p-3">
                          {getTypeIcon(session.type)}
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-semibold text-ink">
                            {session.title}
                          </h3>
                          <div className="flex items-center space-x-2">
                            <span className="px-3 py-1 rounded-full text-sm font-medium border bg-gold-soft text-ink border-gold/30">
                              Completed
                            </span>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getTypeColor(session.type)}`}>
                              {session.type === 'online' ? 'Online' : 'Private'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="flex items-center text-gray-600">
                            <Calendar className="h-5 w-5 mr-2 flex-shrink-0" />
                            <span className="text-base">{session.date}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Clock className="h-5 w-5 mr-2 flex-shrink-0" />
                            <span className="text-base">{session.time}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Clock className="h-5 w-5 mr-2 flex-shrink-0" />
                            <span className="text-base">{session.duration} min</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <User className="h-5 w-5 mr-2 flex-shrink-0" />
                            <span className="text-base">with {session.trainer}</span>
                          </div>
                        </div>



                        {session.notes && (
                          <div className="mt-4 p-3 bg-cream rounded-2xl border border-gray-100">
                            <p className="text-sm text-gray-600">
                              <strong>Notes:</strong> {session.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Cancelled Sessions Grid */}
      {cancelledSessions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12"
        >
          <div className="mb-6">
            <h2 className="font-display text-2xl font-semibold text-ink mb-2">Cancelled Sessions</h2>
            <p className="text-gray-600">Sessions that have been cancelled</p>
          </div>
          <div className="grid gap-6">
            {cancelledSessions.map((session, index) => (
              <motion.div
                key={session.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <GlassCard className="hover:shadow-xl transition-all duration-300 border-red-200 bg-red-50/10">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="flex-shrink-0">
                        <div className="p-3">
                          {session.type === 'online' ? (
                            <Video className="h-5 w-5 text-red-500" />
                          ) : (
                            <MapPin className="h-5 w-5 text-red-500" />
                          )}
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-semibold text-gray-500 line-through">
                            {session.title}
                          </h3>
                          <div className="flex items-center space-x-2">
                            <span className="px-3 py-1 rounded-full text-sm font-medium border bg-red-50 text-red-700 border-red-200">
                              Cancelled
                            </span>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getTypeColor(session.type)}`}>
                              {session.type === 'online' ? 'Online' : 'Private'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="flex items-center text-gray-600">
                            <Calendar className="h-5 w-5 mr-2 flex-shrink-0" />
                            <span className="text-base">{session.date}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Clock className="h-5 w-5 mr-2 flex-shrink-0" />
                            <span className="text-base">{session.time}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Clock className="h-5 w-5 mr-2 flex-shrink-0" />
                            <span className="text-base">{session.duration} min</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <User className="h-5 w-5 mr-2 flex-shrink-0" />
                            <span className="text-base">with {session.trainer}</span>
                          </div>
                        </div>

                        <div className="flex items-center justify-end">
                          <span className="px-4 py-2 text-gray-500 text-sm italic">
                            Session Cancelled
                          </span>
                        </div>

                        {session.notes && (
                          <div className="mt-4 p-3 bg-cream rounded-2xl border border-gray-100">
                            <p className="text-sm text-gray-600">
                              <strong>Notes:</strong> {session.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Session Count */}
      {filteredSessions.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 text-center"
        >
          <p className="text-gray-600">
            Showing {activeSessions.length} active, {completedSessions.length} completed, and {cancelledSessions.length} cancelled sessions
          </p>
        </motion.div>
      )}
    </PanelShell>
  )
}

export default UpcomingSessions 